import { createRouter, createRoute, createRootRoute, redirect } from '@tanstack/react-router';
import App from './App';
import LandingView from './views/LandingView';
import AuthView from './views/AuthView';
import DiscoverView from './views/DiscoverView';
import CampaignView from './views/CampaignView';
import MessagesView from './views/MessagesView';
import PaymentsView from './views/PaymentsView';
import AnalyticsView from './views/AnalyticsView';
import CreatorView from './views/CreatorView';
import OnboardingView from './views/OnboardingView';
import OpportunitiesView from './views/OpportunitiesView';
import ProfileEditView from './views/ProfileEditView';
import CreatorProfileView from './views/CreatorProfileView';
import ProductsView from './views/ProductsView';
import SubscriptionView from './views/SubscriptionView';
import NotFoundView from './views/NotFoundView';
import ErrorView from './views/ErrorView';
import PrivacyView from './views/PrivacyView';
import TermsView from './views/TermsView';
import { resolveAuthedGuard, resolveOnboardingGuard, WAITLIST_REDIRECT } from './lib/routeGuards';

const rootRoute = createRootRoute({ component: App });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingView,
});

// Short URL used in social bios; the landing page scrolls to the hash on mount.
const waitlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/waitlist',
  beforeLoad: () => {
    throw redirect(WAITLIST_REDIRECT);
  },
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyView,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsView,
});

// Pathless layout route: everything under it requires a session.
// In demo mode (no Supabase configured) the guard lets everything through.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ location }) => {
    const target = await resolveAuthedGuard(location);
    if (target) throw redirect(target);
  },
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthView,
});

const feedRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/feed',
  component: DiscoverView,
});

const requestRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/request',
  component: CampaignView,
});

const messagesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/messages',
  component: MessagesView,
});

const paymentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/payments',
  component: PaymentsView,
});

const statsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/stats',
  component: AnalyticsView,
});

const creatorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/creator',
  component: CreatorView,
});

// The creator-facing campaign feed: open briefs from any brand (PIT-76).
const opportunitiesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/opportunities',
  component: OpportunitiesView,
});

// The same profile form in edit mode. No guard of its own: the view redirects
// when there is no row to edit, and RLS scopes every write to the owner.
const profileEditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/creator/edit',
  component: ProfileEditView,
});

// Profile builder for creators who don't have a finished profile yet; the guard
// bounces anyone who already does back to their dashboard.
const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/onboarding',
  beforeLoad: async () => {
    const target = await resolveOnboardingGuard();
    if (target) throw redirect(target);
  },
  component: OnboardingView,
});

const creatorProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/creators/$id',
  component: CreatorProfileView,
});

const productsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/products',
  component: ProductsView,
});

const subscribeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/subscribe',
  component: SubscriptionView,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  waitlistRoute,
  privacyRoute,
  termsRoute,
  authRoute,
  authedRoute.addChildren([
    feedRoute,
    requestRoute,
    messagesRoute,
    paymentsRoute,
    statsRoute,
    creatorRoute,
    profileEditRoute,
    opportunitiesRoute,
    onboardingRoute,
    creatorProfileRoute,
    productsRoute,
    subscribeRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundView,
  defaultErrorComponent: ErrorView,
});
